import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { dashboardService } from '../../services/dashboardService';
import { reservationService, roomService, authService } from '../../services/api';
import ReservationModal from '../reservations/components/ReservationModal';
import NotificationModal from '../reservations/components/NotificationModal';
import { translateRoomType } from '../../constants/roomTypes';

// Lazy loading para componentes pesados - melhoria de performance
const KPICard = lazy(() => import('./components/KPICard'));
const OccupancyChart = lazy(() => import('./components/OccupancyChart'));
const RevenueChart = lazy(() => import('./components/RevenueChart'));
const BookingSourceChart = lazy(() => import('./components/BookingSourceChart'));
const RecentActivity = lazy(() => import('./components/RecentActivity'));
const UpcomingArrivals = lazy(() => import('./components/UpcomingArrivals'));
const RoomAvailabilityCards = lazy(() => import('./components/RoomAvailabilityCards'));

// Componente de Loading otimizado
const LoadingSpinner = React.memo(({ size = 'md', text = 'Carregando...' }) => (
  <div className="flex flex-col items-center justify-center p-8">
    <div className={`animate-spin rounded-full border-4 border-gray-200 border-t-blue-600 ${
      size === 'sm' ? 'h-6 w-6' : 
      size === 'md' ? 'h-8 w-8' : 
      'h-12 w-12'
    }`}></div>
    <p className="mt-3 text-sm text-gray-600 font-medium">{text}</p>
  </div>
));

// Error Boundary para melhor tratamento de erros
class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Dashboard Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Ops! Algo deu errado</h2>
            <p className="text-gray-600 mb-6">
              Ocorreu um erro inesperado. Não se preocupe, seus dados estão seguros.
            </p>
            <Button 
              variant="default" 
              onClick={() => window.location.reload()}
              className="w-full"
            >
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Recarregar Página
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Funções de segurança e sanitização
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input
    .replace(/[<>]/g, '') // Remove caracteres potencialmente perigosos
    .trim()
    .substring(0, 1000); // Limita tamanho
};

const validateHotelData = (hotel) => {
  if (!hotel || !hotel.id) return false;
  if (typeof hotel.id !== 'string' && typeof hotel.id !== 'number') return false;
  return true;
};

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [kpiData, setKpiData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingArrivals, setUpcomingArrivals] = useState([]);
  const [quickStats, setQuickStats] = useState({});
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [availabilityDate, setAvailabilityDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dailyAvailability, setDailyAvailability] = useState([]);
  const [isHotelSwitching, setIsHotelSwitching] = useState(false);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    type: 'success',
    title: '',
    message: '',
    details: null
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Refs para performance
  const refreshIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Estados de loading individuais para melhor UX
  const [loadingStates, setLoadingStates] = useState({
    kpi: true,
    activity: true,
    arrivals: true,
    quickStats: true,
    availability: false
  });

  const [reservationFormData, setReservationFormData] = useState({
     guestName: '',
     guestEmail: '',
     guestPhone: '',
     originCity: '',
     documentNumber: '',
     roomId: '',
     roomNumber: '',
     roomType: '',
     checkInDate: '',
     checkOutDate: '',
     adults: 1,
     children: 0,
     specialRequests: '',
     totalAmount: 0,
     status: 'PENDING',
     paymentStatus: 'PENDING',
     hotelId: null
   });
  const navigate = useNavigate();

  // Cache para dados que não mudam frequentemente
  const [dataCache, setDataCache] = useState({
    kpi: null,
    quickStats: null,
    hotelId: null,
    lastUpdate: null
  });

  // Memoized values para performance
  const selectedHotel = useMemo(() => authService.getSelectedHotel(), []);
  const isAuthenticated = useMemo(() => !!localStorage.getItem('token'), []);

  // Funções otimizadas com useCallback
  const showNotificationModal = useCallback((type, title, message, details = null) => {
    setNotificationData({ type, title, message, details });
    setShowNotification(true);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setShowNotification(false);
  }, []);

  // Função otimizada para carregar dados principais em paralelo
  const loadMainDashboardData = useCallback(async (forceRefresh = false) => {
    try {
      console.log('🚀 Iniciando carregamento otimizado do Dashboard...', forceRefresh ? '(Forçando atualização)' : '');
      const startTime = performance.now();

      const selectedHotel = authService.getSelectedHotel();
      if (!selectedHotel?.id) {
        console.warn('⚠️ Nenhum hotel selecionado');
        return;
      }

      // Verificar cache (dados válidos por 5 minutos)
      // Incluir hotelId na verificação do cache
      const cacheAge = dataCache.lastUpdate ? Date.now() - dataCache.lastUpdate : Infinity;
      const isCacheValid = cacheAge < 5 * 60 * 1000 && dataCache.hotelId === selectedHotel.id;

      if (!forceRefresh && isCacheValid && dataCache.kpi && dataCache.quickStats) {
        console.log('⚡ Usando cache para dados principais (Hotel:', selectedHotel.name, ')');
        setKpiData(dataCache.kpi);
        setQuickStats(dataCache.quickStats);
        setLoadingStates(prev => ({ ...prev, kpi: false, quickStats: false }));
      } else {
        // Carregar dados principais em paralelo
        console.log('🔄 Carregando dados principais da API para hotel:', selectedHotel.name);
        const [stats, quickStatsData] = await Promise.all([
          dashboardService.getDashboardStats({ hotelId: selectedHotel.id }),
          dashboardService.getQuickStats({ hotelId: selectedHotel.id })
        ]);

      // Log para debug
      console.log('📊 Dashboard - Stats recebidos:', {
        occupancyRate: stats.occupancyRate,
        roomsUnavailable: stats.roomsUnavailable,
        totalRooms: stats.totalRooms,
        occupiedRooms: stats.occupiedRooms,
        reservedRooms: stats.reservedRooms
      });
      
      // Transformar estatísticas em KPI cards
      const kpiCards = [
        {
          title: 'Taxa de Ocupação Hoje',
          value: `${stats.occupancyRate || 0}%`,
          subtitle: `${stats.roomsUnavailable || 0} de ${stats.totalRooms || 0} quartos (${stats.occupiedRooms || 0} ocupados + ${stats.reservedRooms || 0} reservados)`,
          icon: 'Bed',
          trend: 'up',
          trendValue: '+5%',
          color: 'primary'
        },
          {
            title: 'Receita do Mês',
            value: `R$ ${(stats.monthlyRevenue || 0).toLocaleString()}`,
            subtitle: `Meta: R$ ${((stats.monthlyRevenue || 0) * 1.2).toLocaleString()}`,
            icon: 'DollarSign',
            trend: 'up',
            trendValue: '+12%',
            color: 'success'
          },
          {
            title: 'Check-ins Hoje',
            value: `${stats.checkInsToday || 0}`,
            subtitle: 'Próximo às 14:00',
            icon: 'UserCheck',
            trend: 'down',
            trendValue: '-2',
            color: 'warning'
          },
          {
            title: 'Pagamentos Pendentes',
            value: `${stats.pendingPayments || 0}`,
            subtitle: 'Aguardando confirmação',
            icon: 'Key',
            trend: 'up',
            trendValue: '+3',
            color: 'primary'
          }
        ];

        setKpiData(kpiCards);
        setQuickStats(quickStatsData);
        
        // Atualizar cache com hotelId
        setDataCache({
          kpi: kpiCards,
          quickStats: quickStatsData,
          hotelId: selectedHotel.id,
          lastUpdate: Date.now()
        });
      }

      setLoadingStates(prev => ({ ...prev, kpi: false, quickStats: false }));
      
      const endTime = performance.now();
      console.log(`✅ Dados principais carregados em ${(endTime - startTime).toFixed(2)}ms`);
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados principais:', error);
      setLoadingStates(prev => ({ ...prev, kpi: false, quickStats: false }));
    }
  }, [dataCache]);

  // Função otimizada para carregar dados secundários (lazy loading)
  const loadSecondaryData = useCallback(async () => {
    try {
      console.log('🔄 Carregando dados secundários...');
      const startTime = performance.now();
      
      const selectedHotel = authService.getSelectedHotel();
      if (!selectedHotel?.id) return;

      // Carregar dados secundários em paralelo
      const [activities, arrivals] = await Promise.all([
        dashboardService.getRecentActivity({ hotelId: selectedHotel.id }),
        dashboardService.getUpcomingArrivals({ hotelId: selectedHotel.id })
      ]);

        setRecentActivity(activities);
        setUpcomingArrivals(arrivals);
      setLoadingStates(prev => ({ ...prev, activity: false, arrivals: false }));
      
      const endTime = performance.now();
      console.log(`✅ Dados secundários carregados em ${(endTime - startTime).toFixed(2)}ms`);
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados secundários:', error);
      setLoadingStates(prev => ({ ...prev, activity: false, arrivals: false }));
    }
  }, []);

  // Função otimizada para carregar disponibilidade
  const loadDailyAvailability = useCallback(async (selectedDate, forceRefresh = false) => {
    try {
      setIsAvailabilityLoading(true);
      setAvailabilityError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setDailyAvailability([]);
        setAvailabilityError('Você não está autenticado. Faça login novamente.');
        return;
      }

      const selectedHotel = authService.getSelectedHotel();
      if (!selectedHotel?.id) {
        setDailyAvailability([]);
        setAvailabilityError('Nenhum hotel selecionado. Selecione um hotel no topo.');
        return;
      }

      if (forceRefresh) {
        setDailyAvailability([]);
        console.log('🔍 Dashboard: Forçando atualização da disponibilidade');
      }

      const list = await roomService.getAvailability(selectedDate, selectedHotel.id);
      setDailyAvailability(list);
      
      if (forceRefresh) {
        console.log('🔍 Dashboard: Disponibilidade atualizada com sucesso após criação de reserva');
      }
    } catch (error) {
      console.error('Erro ao carregar disponibilidade diária:', error);
      const msg = error.response?.data?.message || 'Erro ao carregar disponibilidade.';
      setAvailabilityError(msg);
      setDailyAvailability([]);
    } finally {
      setIsAvailabilityLoading(false);
    }
  }, []);

  // Carregamento inicial otimizado
  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        setIsLoading(true);
        console.log('🚀 Inicializando Dashboard...');
        
        // Carregar dados principais primeiro (mais importantes)
        await loadMainDashboardData();
        
        // Carregar disponibilidade em paralelo (dados críticos)
        loadDailyAvailability(availabilityDate);
        
        // Carregar dados secundários com delay (lazy loading)
        setTimeout(() => {
          loadSecondaryData();
        }, 100);
        
      } catch (error) {
        console.error('❌ Erro na inicialização do Dashboard:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeDashboard();
  }, []);

  // Carregar disponibilidade quando a data mudar
  useEffect(() => {
    if (!isLoading) {
      loadDailyAvailability(availabilityDate);
    }
  }, [availabilityDate, loadDailyAvailability]);

  // Escutar eventos de reserva criada para atualizar o dashboard
  useEffect(() => {
    const handleReservationCreated = (event) => {
      console.log('🔍 Dashboard: Reserva criada detectada:', event.detail);
      
      // Atualizar disponibilidade para a data atual com refresh forçado
      if (availabilityDate) {
        console.log('🔍 Dashboard: Atualizando disponibilidade após criação de reserva');
        loadDailyAvailability(availabilityDate, true);
      }
      
      // Se a reserva foi criada para datas futuras, também atualizar
      const reservationDate = new Date(event.detail.checkInDate);
      const currentDate = new Date(availabilityDate);
      
      if (reservationDate > currentDate) {
        console.log('🔍 Dashboard: Reserva para data futura, atualizando disponibilidade');
        loadDailyAvailability(availabilityDate, true);
      }
    };

    window.addEventListener('reservationCreated', handleReservationCreated);
    
    return () => {
      window.removeEventListener('reservationCreated', handleReservationCreated);
    };
  }, [availabilityDate, loadDailyAvailability]);

  // Recarregar tudo quando o hotel mudar
  useEffect(() => {
    const onHotelChanged = async () => {
      try {
        const selectedHotel = authService.getSelectedHotel();
        console.log('🏨 Dashboard: Mudança de hotel detectada para:', selectedHotel?.name);
        setIsHotelSwitching(true);
        setIsLoading(true);
        setAvailableRooms([]);
        setDailyAvailability([]);
        
        // Limpar cache ao trocar de hotel
        console.log('🧹 Limpando cache do dashboard...');
        setDataCache({ kpi: null, quickStats: null, hotelId: null, lastUpdate: null });
        
        // IMPORTANTE: Forçar atualização dos dados (bypass cache)
        await loadMainDashboardData(true); // true = forceRefresh
        await loadDailyAvailability(availabilityDate);
        
        // Recarregar dados secundários
        await loadSecondaryData();
        
        console.log('✅ Dashboard: Dados recarregados após mudança de hotel');
        
      } catch (err) {
        console.error('Erro ao recarregar dados após troca de hotel:', err);
      } finally {
        setIsHotelSwitching(false);
        setIsLoading(false);
      }
    };

    // Adicionar listener para o evento customizado 'hotelChanged'
    window.addEventListener('hotelChanged', onHotelChanged);
    
    // Também manter listener para mudanças no localStorage (para outras abas)
    const handleStorage = (e) => {
      if (e.key === 'selectedHotel') {
        onHotelChanged();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('hotelChanged', onHotelChanged);
      window.removeEventListener('storage', handleStorage);
    };
  }, [availabilityDate, loadMainDashboardData, loadDailyAvailability, loadSecondaryData]);

  // Função otimizada para criar reserva
  const handleCreateReservation = useCallback(async () => {
    try {
      console.log('🔍 Carregando quartos disponíveis...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        showNotificationModal('error', 'Sessão Expirada', 'Sua sessão expirou. Faça login novamente.');
        navigate('/login');
        return;
      }

      const selectedHotel = authService.getSelectedHotel();
      if (!selectedHotel?.id) {
        showNotificationModal('warning', 'Hotel Não Selecionado', 'Selecione um hotel no topo para criar uma reserva.');
        return;
      }

      // Limpar formulário antes de abrir modal
      console.log('🔍 Limpando formulário de reserva...');
      setReservationFormData({
        guestName: '',
        guestEmail: '',
        guestPhone: '',
        originCity: '',
        documentNumber: '',
        roomId: '',
        roomNumber: '',
        roomType: '',
        checkInDate: '',
        checkOutDate: '',
        adults: 1,
        children: 0,
        specialRequests: '',
        totalAmount: 0,
        status: 'PENDING',
        paymentStatus: 'PENDING',
        hotelId: selectedHotel.id
      });

      // Carregar quartos em paralelo com cache
      const rooms = await roomService.getAll({ hotelId: selectedHotel.id });
      const availableRoomsList = rooms.filter(room => room.status === 'AVAILABLE');
      
      const formattedRooms = availableRoomsList.map(room => ({
        value: room.id.toString(),
        label: `Quarto ${room.number} - ${translateRoomType(room.type)} (R$ ${room.couplePrice})`
      }));
      
      setAvailableRooms(formattedRooms);
      setShowReservationModal(true);
    } catch (error) {
      console.error('❌ Erro ao carregar quartos:', error);
      showNotificationModal('error', 'Erro ao Carregar Quartos', 'Erro ao carregar quartos disponíveis. Tente novamente.');
    }
  }, [navigate]);

  const handleReservationFormChange = (field, value) => {
    // Formatação automática do telefone
    if (field === 'guestPhone' && value) {
      // Remove tudo que não é número
      const numbers = value.replace(/\D/g, '');
      
      // Aplica formatação brasileira
      if (numbers.length <= 11) {
        if (numbers.length <= 2) {
          value = `(${numbers}`;
        } else if (numbers.length <= 6) {
          value = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        } else if (numbers.length <= 10) {
          value = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        } else {
          value = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
        }
      }
    }
    
    setReservationFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoomSelection = async (roomId) => {
    try {
      if (roomId) {
      const selectedRoom = await roomService.getById(roomId);
        // Não pré-calcular totalAmount aqui; deixar o ReservationModal
        // calcular corretamente com base em adultos/crianças/idades.
        setReservationFormData(prev => ({
          ...prev,
          roomId: roomId,
          roomNumber: selectedRoom.number,
          roomType: selectedRoom.type,
          totalAmount: 0
        }));
      } else {
        setReservationFormData(prev => ({
          ...prev,
          roomId: '',
          roomNumber: '',
          roomType: '',
          totalAmount: 0
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados do quarto:', error);
    }
  };

  const translateRoomStatus = (status) => {
    const statusMap = {
      'AVAILABLE': 'Disponível',
      'OCCUPIED': 'Ocupado',
      'MAINTENANCE': 'Manutenção',
      'CLEANING': 'Limpeza',
      'RESERVED': 'Reservado'
    };
    return statusMap[status] || status;
  };


  const handleSaveReservationFromModal = async (reservationData) => {
    try {
      console.log('🔍 Dashboard - Recebendo dados do modal:', reservationData);
      
      // O ReservationModal já fez toda a validação, então podemos confiar nos dados
      const newReservation = await reservationService.create(reservationData);
      
      console.log('✅ Dashboard - Reserva criada com sucesso:', newReservation);
      
      // Recarregar dados do dashboard
      await loadMainDashboardData();
      await loadDailyAvailability(availabilityDate, true);
      
      // Não fechar modal aqui - o modal vai fechar após mostrar mensagem de sucesso
      // Não mostrar mensagem aqui - o modal vai mostrar
      
    } catch (error) {
      console.error('❌ Dashboard - Erro ao criar reserva:', error);
      console.error('❌ Dashboard - Detalhes do erro:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status
      });
      
      // Re-lançar o erro para que o modal possa capturá-lo
      // O erro já contém as informações de conflito (se houver)
      throw error;
    }
  };

  // Timer para atualizar hora atual
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          onCreateReservation={handleCreateReservation} 
          onMenuClick={() => setIsMobileMenuOpen(true)}
        />
        <Sidebar 
          isOpen={isMobileMenuOpen} 
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="lg:ml-60 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="text-muted-foreground">Carregando dashboard...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onCreateReservation={handleCreateReservation} 
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />
      <Sidebar 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)}
        onCollapseChange={setIsSidebarCollapsed}
      />
      
      <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-4 md:p-6">
          {/* Breadcrumb */}
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 md:mb-8">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Dashboard</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {isHotelSwitching ? 'Trocando de hotel e atualizando dados...' : 'Bem-vindo de volta! Aqui está o resumo das suas operações hoteleiras.'}
              </p>
            </div>
            
            <div className="flex items-center space-x-2 md:space-x-4 mt-4 lg:mt-0">
              {/* Clock - Full on desktop, time only on tablet */}
              <div className="hidden lg:flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                <span>
                  {currentTime.toLocaleDateString('pt-BR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })} - {currentTime.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              
              {/* Clock - Compact for tablet */}
              <div className="flex lg:hidden items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                <span>
                  {currentTime.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              
              {/* Refresh Button - Icon only on mobile/tablet */}
              <Button 
                variant="outline" 
                size="sm"
                iconName="RefreshCw" 
                iconPosition="left"
                onClick={() => loadDailyAvailability(availabilityDate, true)}
                disabled={isAvailabilityLoading}
                className={`hidden md:flex ${isAvailabilityLoading ? 'opacity-75' : ''}`}
              >
                {isAvailabilityLoading ? 'Atualizando...' : 'Atualizar'}
              </Button>
              
              {/* Mobile/Tablet - Icon only */}
              <Button 
                variant="outline" 
                size="sm"
                iconName="RefreshCw" 
                onClick={() => loadDailyAvailability(availabilityDate, true)}
                disabled={isAvailabilityLoading}
                className={`md:hidden ${isAvailabilityLoading ? 'opacity-75' : ''}`}
                title="Atualizar"
              />
              
              {/* Create Reservation - Full button on desktop */}
              <Button 
                variant="default" 
                size="sm"
                iconName="Plus" 
                iconPosition="left"
                onClick={handleCreateReservation}
                className="hidden md:flex"
              >
                Reserva
              </Button>
              
              {/* Create Reservation - Icon only on mobile/tablet */}
              <Button 
                variant="default" 
                size="sm"
                iconName="Plus" 
                onClick={handleCreateReservation}
                className="md:hidden"
                title="Reserva"
              />
            </div>
          </div>

          {/* Disponibilidade dos Quartos - Agora logo abaixo do título */}
          <div className="mb-8">
            <RoomAvailabilityCards
              dailyAvailability={dailyAvailability}
              availabilityDate={availabilityDate}
              isAvailabilityLoading={isAvailabilityLoading}
              availabilityError={availabilityError}
              onRoomSelect={async (room) => {
                // Pré-selecionar quarto e datas com base no dia
                console.log('🔍 Quarto selecionado para reserva:', room);
                console.log('🔍 Data de disponibilidade atual:', availabilityDate);
                
                await handleRoomSelection(room.roomId);
                const nextDay = new Date(availabilityDate);
                nextDay.setDate(nextDay.getDate() + 1);
                const nextDayStr = nextDay.toISOString().split('T')[0];
                
                console.log('🔍 Definindo datas:', {
                  checkInDate: availabilityDate,
                  checkOutDate: nextDayStr
                });
                
                setReservationFormData(prev => ({
                  ...prev,
                  checkInDate: availabilityDate,
                  checkOutDate: nextDayStr
                }));
                setShowReservationModal(true);
              }}
              onDateChange={(newDate) => {
                console.log('🔍 Dashboard: onDateChange chamado com data:', newDate);
                setAvailabilityDate(newDate);
                // Se a data for a mesma, force o reload
                if (newDate === availabilityDate) {
                  console.log('🔍 Dashboard: Data igual, forçando reload...');
                  loadDailyAvailability(newDate, true);
                }
              }}
            />
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {kpiData.map((kpi, index) => (
              <Suspense key={index} fallback={<LoadingSpinner size="sm" />}>
                <KPICard
                  title={kpi.title}
                  value={kpi.value}
                  subtitle={kpi.subtitle}
                  icon={kpi.icon}
                  trend={kpi.trend}
                  trendValue={kpi.trendValue}
                  color={kpi.color}
                />
              </Suspense>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            {/* Charts Section */}
            <div className="xl:col-span-2 space-y-4 md:space-y-6">
              <Suspense fallback={<LoadingSpinner text="Carregando gráficos..." />}>
                <OccupancyChart />
                <RevenueChart />
              </Suspense>
            </div>
            
            {/* Right Sidebar */}
            <div className="space-y-4 md:space-y-6">
              <Suspense fallback={<LoadingSpinner text="Carregando dados..." />}>
                <BookingSourceChart />
                <RecentActivity activities={recentActivity} />
              </Suspense>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Suspense fallback={<LoadingSpinner text="Carregando chegadas..." />}>
              <UpcomingArrivals arrivals={upcomingArrivals} />
            </Suspense>
            
            {/* Quick Stats */}
            <div className="bg-card border border-border rounded-lg p-4 sm:p-6 shadow-elevation-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <h3 className="text-lg font-semibold text-foreground">Estatísticas Rápidas</h3>
                <Button variant="ghost" size="sm" iconName="BarChart3" iconPosition="left" className="flex-shrink-0 text-xs sm:text-sm">
                  Ver Relatório
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <div className="text-center min-w-0">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-primary mb-1 truncate" title={`${quickStats.guestSatisfaction || 0}%`}>
                    {quickStats.guestSatisfaction || 0}%
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">Ocupação do Mês</div>
                </div>
                
                <div className="text-center min-w-0">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-success mb-1 truncate" title={quickStats.averageRating || 0}>
                    R$ {(quickStats.averageRating || 0).toLocaleString('pt-BR')}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">Receita do Mês</div>
                </div>
                
                <div className="text-center min-w-0">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-warning mb-1 truncate" title={quickStats.monthlyBookings || 0}>
                    {quickStats.monthlyBookings || 0}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">Reservas Este Mês</div>
                </div>
                
                <div className="text-center min-w-0">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-secondary mb-1 truncate" title={`R$ ${quickStats.averageDailyRate || 0}`}>
                    R$ {quickStats.averageDailyRate || 0}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">Diária Média</div>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Última atualização:</span>
                  <span className="text-foreground font-medium">Agora mesmo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Disponibilidade por dia */}
          {/* This section is now moved above the KPI cards */}
        </div>
      </main>

      {/* Reservation Modal */}
      <ReservationModal
        isOpen={showReservationModal}
        onClose={() => {
          console.log('🔍 Dashboard - Fechando modal e limpando formulário...');
          setShowReservationModal(false);
          // Limpar formulário ao fechar
          setReservationFormData({
            guestName: '',
            guestEmail: '',
            guestPhone: '',
            originCity: '',
            documentNumber: '',
            roomId: '',
            roomNumber: '',
            roomType: '',
            checkInDate: '',
            checkOutDate: '',
            adults: 1,
            children: 0,
            specialRequests: '',
            totalAmount: 0,
            status: 'PENDING',
            paymentStatus: 'PENDING',
            hotelId: null
          });
          // Recarregar dados do dashboard ao fechar modal
          loadMainDashboardData();
          loadDailyAvailability(availabilityDate, true);
        }}
        reservation={reservationFormData}
        onSave={handleSaveReservationFromModal}
        mode="create"
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={handleCloseNotification}
        type={notificationData.type}
        title={notificationData.title}
        message={notificationData.message}
        details={notificationData.details}
      />
    </div>
  );
};

export default Dashboard;